import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';

/** Outcome of a failed backend push: the status to apply and why. */
export interface SyncFailure {
  status: ReplicationSyncStatus.Failed | ReplicationSyncStatus.UnSynced;
  message: string;
}

/**
 * Classify a backend-sync failure for every replicable entity.
 *
 * `UnSynced` is the *initial* state of a record that has not reached the
 * backend yet, so the pipeline only re-applies it when the request genuinely
 * never got there — a dropped connection, a timeout, a request that produced no
 * response. Anything the backend actually answered with (a validation error, a
 * conflict, a server error) becomes `Failed` carrying the reason, so a rejected
 * record is distinguishable from a brand-new one instead of quietly blending in.
 *
 * `Failed` is a marker, not a terminal state: every sync cycle retries `Failed`
 * records alongside `UnSynced` ones, so a rejection the backend later accepts
 * still goes through and nothing is stranded.
 */
export function classifySyncFailure(error: any): SyncFailure {
  // A numeric HTTP status greater than 0 means a response came back from the
  // server; Angular reports connection-level errors as status 0.
  const httpStatus = typeof error?.status === 'number' ? error.status : 0;
  return {
    status: httpStatus > 0 ? ReplicationSyncStatus.Failed : ReplicationSyncStatus.UnSynced,
    message: describeSyncError(error)
  };
}

export function describeSyncError(error: any): string {
  return error?.error?.message ?? error?.message ?? 'Backend sync failed';
}
